import React, {useState} from 'react';
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {invalidate, prefetch} from "services/query-client";
import {MapIcon} from "@heroicons/react/outline";
import Button from "components/Button";
import {PlusIcon} from "@heroicons/react/solid";
import {useNavigate} from "react-router-dom";
import {Trail, TrailAPIData, TrailUpdateAPIData} from "types/Trail";
import DeleteTrail from "pages/Trail/Delete";
import BooleanIndicator from "components/BooleanIndicator";
import Badge from 'components/Badge';
import ReorderTrail from "../Reorder";
import {useNotifications} from "../../../context/NotificationContext";
import {useOperator} from "../../../context/OperatorContext";
import {toTitleCase} from "../../../services/string";
import {UnitSystem} from "../../../types/UnitSystem";
import ConvertedValue from "../../../components/ConvertedValue";
import FeedbackBadge from "../../../components/FeedbackBadge";
import {useAuth} from "../../../context/AuthContext";
import NotAuthorised from "../../NotAuthorised";
import TrailContextMenu from "../../../components/menus/TrailContextMenu";

// Types ----

type TrailUpdate = {
  id: number
  data: TrailUpdateAPIData
}

// Component ----

function TrailList() {
  const {termFor, settings} = useOperator();
  const auth = useAuth();
  const navigate = useNavigate();
  const {notify} = useNotifications();

  const [reorderingTrail, setReorderingTrail] = useState<Trail | null>(null);
  const [deletingTrail, setDeletingTrail] = useState<Trail | null>(null);
  const [page, setPage] = useState<number>(1);

  const result = useQuery(
    ['trails.all', {page}],
    () => ngage.trails.filtered({page}),
    {keepPreviousData: true, enabled: auth.can('trails:read')}
  )

  const {mutateAsync: updateTrail} = useMutation(
    (update: TrailUpdate) => ngage.trails.update(update.id, update.data)
  )

  async function toggleTrailPublishStatus(trail: Trail) {
    const publishing = !trail.is_published

    try {
      await updateTrail({
        id: trail.id,
        data: {is_published: publishing}
      })

      void invalidate('trails.all')

      if (publishing) {
        notify("success", `${termFor('Trail')} Published`, `${trail.name} has been published`)
      } else {
        notify("success", `${termFor('Trail')} Unpublished`, `${trail.name} has been unpublished`)
      }
    } catch (error: any) {
      notify("error", "Something Went Wrong", `${trail.name} published status could not be changed`)
    }
  }

  async function toggleTrailFeatureStatus(trail: Trail) {
    const featuring = !trail.is_featured

    try {
      await updateTrail({
        id: trail.id,
        data: {is_featured: featuring}
      })

      void invalidate('trails.all')

      if (featuring) {
        notify("success", `${termFor('Trail')} Featured`, `${trail.name} is now a featured ${termFor('trail')}`)
      } else {
        notify("success", `${termFor('Trail')} Not Featured`, `${trail.name} is no longer a featured ${termFor('trail')}`)
      }
    } catch (error: any) {
      notify("error", "Something Went Wrong", `${trail.name} feature status could not be changed`)
    }
  }

  // Prevent unauthorised viewing
  if (auth.cannot('trails:read')) {
    return <NotAuthorised/>
  }

  // Prefetch next and previous pages
  prefetch('trails', {page}, ngage.trails.filtered)

  // Prepare page props
  const title = `${termFor('Trails')}`

  const actions = (
    <>
      {auth.can('trails:write') &&
        <Button icon={PlusIcon} onClick={() => navigate('/trails/new')}>New</Button>
      }
    </>
  )

  const breadcrumbs = {
    'App Content': '/',
    'Site': '/',
    [termFor('Trails')]: '/trails'
  }

  return (
    <Page
      title={title}
      actions={actions}
      breadcrumbs={breadcrumbs}
    >
      <Table paginationData={result.data} setPage={setPage}>
        <TableHead>
          <TableRow>
            <TableHeader>Name</TableHeader>
            <TableHeader>Type</TableHeader>
            <TableHeader>Rating</TableHeader>
            <TableHeader>Length</TableHeader>
            <TableHeader>Transport</TableHeader>
            <TableHeader alignment={TableHeader.alignments.CENTER}>Published</TableHeader>
            <TableHeader alignment={TableHeader.alignments.CENTER}>Featured</TableHeader>
            <TableHeader alignment={TableHeader.alignments.CENTER}>Feedback</TableHeader>
            <TableHeader/>
          </TableRow>
        </TableHead>

        <TableBody isLoading={result.isLoading}>

          {result?.data && result.data.data.length < 1 ?
            <TableRow index={0}>
              <TableData colSpan={9}>
                <div className="py-4 flex flex-col justify-center items-center">
                  <MapIcon className="h-8 w-8 text-gray-400 mb-2"/>
                  <span className="text-sm font-medium text-gray-600">No {termFor('trails')}</span>
                </div>
              </TableData>
            </TableRow>
            : result?.data?.data.map((trail, index) => (
              <TableRow key={trail.id} index={index} highlight>
                <TableData>
                  <div className="flex">
                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                      <TrailContextMenu
                        trail={trail}
                        onReorderTrails={() => setReorderingTrail(trail)}
                        onPublishTrail={() => toggleTrailPublishStatus(trail)}
                        onFeatureTrail={() => toggleTrailFeatureStatus(trail)}
                        onDeleteTrail={() => setDeletingTrail(trail)}
                        position="left"
                      />
                    </div>

                    <div className="flex group-hover:translate-x-1 transition-all">
                      <MapIcon className="h-5 w-5 mr-2 text-gray-500"/> {trail.name}
                    </div>
                  </div>
                </TableData>
                <TableData>
                  {toTitleCase(trail.type)}
                </TableData>
                <TableData>
                  {trail.rating ?
                    <Badge
                      text={trail.rating.name}
                      colour={trail.rating.colour}
                      textColour={trail.rating.text_colour}
                    /> :
                    <Badge text="Unrated" colour="#6d6d6d"/>
                  }
                </TableData>
                <TableData>
                  <ConvertedValue
                    value={trail.length}
                    fromUnit={'meters'}
                    toUnit={settings?.localisation.length_units === UnitSystem.METRIC ? 'meters' : 'feet'}
                  />
                </TableData>
                <TableData>{trail.transport_modes.join(', ')}</TableData>
                <TableData alignment={TableData.alignments.CENTER}>
                  <BooleanIndicator value={trail.is_published}/>
                </TableData>
                <TableData alignment={TableData.alignments.CENTER}>
                  <BooleanIndicator value={trail.is_featured}/>
                </TableData>
                <TableData alignment={TableData.alignments.CENTER}>
                  <FeedbackBadge
                    count={trail.aggregated_feedback ? trail.aggregated_feedback.count : null}
                    positiveRate={trail.aggregated_feedback ? trail.aggregated_feedback.positive_rate : null}
                  />
                </TableData>
                <TableData alignment={TableData.alignments.RIGHT}>
                  <TrailContextMenu
                    trail={trail}
                    onReorderTrails={() => setReorderingTrail(trail)}
                    onPublishTrail={() => toggleTrailPublishStatus(trail)}
                    onFeatureTrail={() => toggleTrailFeatureStatus(trail)}
                    onDeleteTrail={() => setDeletingTrail(trail)}
                  />
                </TableData>
              </TableRow>
            ))}

        </TableBody>
      </Table>

      {/* Trail reordering modal */}
      <ReorderTrail
        trail={reorderingTrail}
        show={!!reorderingTrail}
        onCancel={() => setReorderingTrail(null)}
        onReorder={() => setReorderingTrail(null)}
        onReorderFail={() => setReorderingTrail(null)}
      />

      {/* Delete confirmation modal */}
      <DeleteTrail
        trail={deletingTrail}
        show={!!deletingTrail}
        onCancel={() => setDeletingTrail(null)}
        onDelete={() => setDeletingTrail(null)}
        onDeleteFail={() => setDeletingTrail(null)}
      />
    </Page>
  );
}

export default TrailList;